/**
 * Fiscal Year Selector Component
 *
 * Dropdown for selecting a fiscal year period for emissions data filtering.
 * Shared across Dashboard, Scope pages, and Insights pages.
 */

import { useMemo, useCallback } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@g17eco/ui-modern/components/ui/dropdown-menu';
import { cn } from '@g17eco/ui-modern/lib/utils';

// Get most recent COMPLETED fiscal year (current year may be incomplete)
export const getCompletedFiscalYear = (): number => new Date().getFullYear() - 1;

// Generate completed fiscal year options (previous year + 4 more years back)
export const getFiscalYearOptions = (count = 5): Array<{ value: number; label: string }> =>
  Array.from({ length: count }, (_, i) => {
    const year = getCompletedFiscalYear() - i;
    return { value: year, label: `FY ${year}` };
  });

// Helper to get period dates for a specific fiscal year
export const getFiscalYearPeriod = (year: number): { start: string; end: string } => ({
  start: `${year}-01-01`,
  end: `${year}-12-31`,
});

// Helper to get previous year period dates for YoY comparison
export const getPreviousYearPeriod = (year: number): { start: string; end: string } => ({
  start: `${year - 1}-01-01`,
  end: `${year - 1}-12-31`,
});

interface FiscalYearSelectorProps {
  selectedYear: number;
  onYearChange: (year: number) => void;
  years?: number[];
  className?: string;
}

export const FiscalYearSelector = ({
  selectedYear,
  onYearChange,
  years,
  className = '',
}: FiscalYearSelectorProps) => {
  const options = useMemo(
    () =>
      years
        ? years.map((y) => ({ value: y, label: `FY ${y}` }))
        : getFiscalYearOptions(),
    [years]
  );

  const handleYearChange = useCallback(
    (year: number) => {
      onYearChange(year);
    },
    [onYearChange]
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='outline' className={className}>
          <Calendar className='tw:size-4' />
          FY {selectedYear}
          <ChevronDown className='tw:size-3.5' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => handleYearChange(option.value)}
            className={cn(option.value === selectedYear && 'tw:font-semibold tw:text-emerald-600')}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
